import { type ReactNode } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { CircularProgress, Box } from '@mui/material';
import { msalInstance, loginRequest } from './msalConfig';

/**
 * Inner guard component that enforces authentication.
 *
 * Behavior:
 * - Not authenticated + no interaction in progress → trigger loginRedirect (auto-redirect, no button)
 * - Auth interaction in progress → show centered spinner
 * - Authenticated → render children
 *
 * There is intentionally NO login button or landing page. Users are redirected immediately.
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated && inProgress === InteractionStatus.None) {
    // Trigger redirect immediately — no landing page
    void instance.loginRedirect(loginRequest);
    return null;
  }

  if (!isAuthenticated && inProgress !== InteractionStatus.None) {
    // Auth flow in progress (redirect returning, interaction happening)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}

/**
 * AuthProvider wraps the app with MsalProvider and enforces authentication via AuthGuard.
 * Must be the outermost provider so all children can use MSAL hooks.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthGuard>{children}</AuthGuard>
    </MsalProvider>
  );
}
